import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import StyledButton from "../components/button";
import Dropdown from "./dropdown";
import ReceiptPreview from "./ReceiptPreview";

interface Props {
  uid: string;
}

interface PurchaseLog {
  id: string;
  createdAt: { seconds: number };
  rawText?: string;
  store?: string;
  items?: { label: string; price: number }[];
  tax?: number;
  total?: number;
  [key: string]: any;
}

const PurchaseHistory: React.FC<Props> = ({ uid }) => {
  const [groupedLogs, setGroupedLogs] = useState<Record<string, PurchaseLog[]>>(
    {}
  );
  const [showRawMap, setShowRawMap] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();

  const fetchLogs = useCallback(async () => {
    const q = query(
      collection(db, `users/${uid}/purchases`),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    const items = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        createdAt: data.createdAt,
        ...data,
      } as PurchaseLog;
    });

    const grouped: Record<string, PurchaseLog[]> = {};
    for (const log of items) {
      const dateStr = new Date(log.createdAt.seconds * 1000).toDateString();
      if (!grouped[dateStr]) grouped[dateStr] = [];
      grouped[dateStr].push(log);
    }

    const sortedDates = Object.keys(grouped)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .slice(0, 5);

    const trimmedGrouped: Record<string, PurchaseLog[]> = {};
    sortedDates.forEach((date) => {
      trimmedGrouped[date] = grouped[date];
    });

    setGroupedLogs(trimmedGrouped);
  }, [uid]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleRaw = (logId: string) => {
    setShowRawMap((prev) => ({ ...prev, [logId]: !prev[logId] }));
  };

  const handleDelete = async (logId: string) => {
    if (!uid) return;
    const ref = doc(db, `users/${uid}/purchases`, logId);
    await deleteDoc(ref);
    await fetchLogs(); // Refresh list
  };

  return (
    <div>
      <h2>Recent Purchases</h2>
      {Object.keys(groupedLogs).length === 0 ? (
        <p>No purchases logged yet.</p>
      ) : (
        Object.entries(groupedLogs).map(([date, logs], i) => (
          <Dropdown key={i} title={date}>
            {logs.map((log, j) => {
              const { items, tax, total } = {
                items: log.items || [],
                tax: log.tax ?? 0,
                total: log.total ?? 0,
              };

              const isRawVisible = showRawMap[log.id] || false;

              return (
                <div key={j} style={{ marginBottom: "2rem" }}>
                  <div style={{ marginBottom: "8px" }}>
                    <StyledButton onClick={() => toggleRaw(log.id)}>
                      {isRawVisible ? "🔽 Hide Raw OCR" : "🔼 Show Raw OCR"}
                    </StyledButton>
                  </div>

                  {isRawVisible && log.rawText && (
                    <div>
                      <p style={{ fontWeight: "bold" }}>Raw OCR Output:</p>
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          backgroundColor: "#f8f8f8",
                          padding: "10px",
                        }}
                      >
                        {log.rawText}
                      </pre>
                    </div>
                  )}

                  {items.length > 0 && (
                    <ReceiptPreview
                      store={log.store || log.title || "Store"}
                      items={items}
                      tax={tax}
                      total={total}
                    />
                  )}

                  <div
                    style={{ marginTop: "10px", display: "flex", gap: "10px" }}
                  >
                    <button
                      onClick={() => navigate(`/foodbudget/edit/${log.id}`)}
                    >
                      ✏️ Edit
                    </button>
                    <button onClick={() => handleDelete(log.id)}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </Dropdown>
        ))
      )}
    </div>
  );
};

export default PurchaseHistory;
